import { MIN_CHUNK_BUFFER } from '../constants/backend';

interface BufferedChunk {
    offsetInAudioStreamMsec: number;
    audioChunk: Uint8Array;
    channel: number;
    send?: boolean;
}

export default class ChunkPairsBuffer {
    private _bufferMaxFrames: number;
    private _optInGracePeriodMsec: number;
    private _data: BufferedChunk[];

    constructor(optInGracePeriodMsec: number) {
        this._bufferMaxFrames = 10 * 60 * 100; // In case there is no data on the length of audio chunks
        this._optInGracePeriodMsec = optInGracePeriodMsec;
        this._data = [];
    }

    setOptInGracePeriodMsec(optInGracePeriodMsec: number) {
        this._optInGracePeriodMsec = optInGracePeriodMsec < MIN_CHUNK_BUFFER ? MIN_CHUNK_BUFFER : optInGracePeriodMsec;
    }

    getOptInGracePeriodMsec() {
        return this._optInGracePeriodMsec;
    }

    enqueue(chunk: BufferedChunk) {
        this._data.push(chunk);

        const dataLen = this._data.length;
        const lastChunkOffsetMsec = this._data[dataLen - 1]?.offsetInAudioStreamMsec || 0;

        if (dataLen > 0 && this._optInGracePeriodMsec > 0) {
            let i = -1;
            for (let idx = dataLen - 1; idx >= 0; idx--) {
                const audioOverLimit = lastChunkOffsetMsec - this._data[idx].offsetInAudioStreamMsec;
                if (audioOverLimit > this._optInGracePeriodMsec) {
                    i = idx;
                    break;
                }
            }
            if (i > 0) {
                this._data = this._data.slice(i);
            }
        }

        if (this._data.length >= this._bufferMaxFrames) {
            this.dequeue();
        }
    }

    dequeue(): BufferedChunk | undefined {
        return this._data.shift();
    }

    size() {
        return this._data.length;
    }

    countOfSend() {
        let counter = 0;
        this._data.forEach((chunk: BufferedChunk) => {
            if (chunk.send) {
                counter++;
            }
        });

        return counter;
    }

    mergeToOne() {
        let totalLength = 0;
        let offsetInAudioStreamMsec = 0;
        const dataLen = this._data.length;

        if (dataLen === 0) {
            return;
        }

        for (let index = dataLen - 1; index >= 0; index--) {
            const chunk = this._data[index] as BufferedChunk;
            if (chunk.send) {
                break;
            }
            offsetInAudioStreamMsec = chunk.offsetInAudioStreamMsec;
            totalLength += chunk.audioChunk.length;
        }

        const combinedAudio = new Uint8Array(totalLength);
        let offset = totalLength;

        for (let index = dataLen - 1; index >= 0; index--) {
            const chunk = this._data[index] as BufferedChunk;
            if (chunk.send) {
                break;
            }
            offset -= chunk.audioChunk.length;
            combinedAudio.set(chunk.audioChunk, offset);

            // TODO: mark item as send only after it was dispatched to socket
            // eslint-disable-next-line no-param-reassign
            chunk.send = true;
        }

        return {
            offsetInAudioStreamMsec,
            audioChunk: combinedAudio,
            channel: this._data[0].channel
        };
    }

    markAllUnSend() {
        this._data.forEach((chunk: BufferedChunk) => {
            // eslint-disable-next-line no-param-reassign
            chunk.send = false;
        });
    }

    reset() {
        this._data = [];
    }

    // Absolute stream position of the most recent buffered chunk, regardless of whether it was
    // already sent. Use this to anchor a restarted recorder to where the audio stream actually
    // left off. (Historically computed as "first-unsent offset + buffer duration", which silently
    // collapsed to just the duration in keyphrase mode — where the grace buffer was already streamed
    // chunk-by-chunk for phrase spotting, so every chunk was flagged sent.)
    getLastOffsetMsec() {
        if (!this._data || this._data.length === 0) {
            return 0;
        }

        return this._data[this._data.length - 1].offsetInAudioStreamMsec;
    }
}
